import axios from 'axios';
// Prefer environment variable to choose API host; fallback to localhost
const API_HOST = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const API_BASE_URL = `${API_HOST.replace(/\/$/, '')}/api`;

const api = axios.create({
  baseURL: API_BASE_URL,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const login = (credentials) => api.post('/token/', credentials);

export const fetchCurrentSeasonId = async () => {
  try {
    const response = await api.get('/current-season-id/');
    if (response?.data?.id) {
      return response;
    }
  } catch (error) {
    console.warn('Primary current season ID endpoint failed, falling back to /seasons/.', error?.message || error);
  }

  const seasonsResponse = await api.get('/seasons/');
  const seasons = Array.isArray(seasonsResponse.data) ? seasonsResponse.data : [];
  const latestSeason = seasons.length > 0
    ? seasons.reduce((maxSeason, season) =>
      season.id > maxSeason.id ? season : maxSeason, seasons[0])
    : null;

  return { data: { id: latestSeason ? latestSeason.id : null } };
};

export const fetchSeasons = () => api.get('/seasons/');
export const fetchSeasonById = (seasonId) => api.get(`/seasons/${seasonId}/`);
export const createSeason = (seasonData) => api.post('/create-season/', seasonData);
export const updateSeason = (seasonId, seasonData) => api.put(`/update-season/${seasonId}/`, seasonData);
export const deleteSeason = (seasonId) => api.delete(`/delete-season/${seasonId}/`);
export const fetchSeasonRules = (seasonId) => api.get(`/seasons/${seasonId}/rules/`);
export const updateSeasonRules = (seasonId, rulesData) => api.put(`/seasons/${seasonId}/rules/`, rulesData);
export const fetchSeasonWorkflowEvents = (seasonId) => api.get(`/seasons/${seasonId}/workflow-events/`);
export const transitionSeasonPhase = (seasonId, transitionData) =>
  api.post(`/seasons/${seasonId}/workflow/transition/`, transitionData);
export const seedPlayoffsFromStandings = (seasonId, payload = { clear_existing: true }) =>
  api.post(`/seasons/${seasonId}/playoffs/seed-from-standings/`, payload);
export const progressSeasonPlayoffs = (seasonId) =>
  api.post(`/seasons/${seasonId}/playoffs/progress/`);
export const createPredefinedPlayoffs = (payload) =>
  api.post('/create-predefined-playoffs/', payload);
export const fetchPlayoffRounds = (seasonId) =>
  api.get(`/playoff-rounds/${seasonId}/`);
export const updatePlayoffMatch = (matchId, data) =>
  api.put(`/update-playoff-match/${matchId}/`, data);
export const updatePlayoffRound = (roundId, data) =>
  api.put(`/update-playoff-round/${roundId}/`, data);
export const recordGoldenSet = (data) =>
  api.post('/record-golden-set/', data);


export const fetchTeams = async (seasonId) => {
  try {
    // Preferred: nested endpoint
    const res = await api.get(`/seasons/${seasonId}/teams/`);
    return res;
  } catch (e) {
    // Fallback 1: query by season_id
    try {
      const res = await api.get('/teams/', { params: { season_id: seasonId } });
      return res;
    } catch (e2) {
      // Fallback 2: fetch all and filter client-side if season relation exists
      const resAll = await api.get('/teams/');
      const data = Array.isArray(resAll.data)
        ? resAll.data.filter(t => (t.season && (t.season.id === Number(seasonId) || t.season === Number(seasonId))))
        : [];
      return { data };
    }
  }
};
export const fetchTeam = (teamId) => api.get(`/teams/${teamId}/`);
export const createTeam = (teamData) => api.post(`/create-team/`, teamData);  // Ensure this endpoint matches the backend
export const updateTeam = (teamId, teamData) => api.put(`/update-team/${teamId}/`, teamData);
export const deleteTeam = (teamId) => api.delete(`/delete-team/${teamId}/`);

export const fetchPlayers = () => api.get('/players/'); // Add this line
export const createPlayer = (playerData) => api.post('/create-player/', playerData);
export const updatePlayer = (playerId, playerData) => api.put(`/update-player/${playerId}/`, playerData);
export const deletePlayer = (playerId) => api.delete(`/delete-player/${playerId}/`);

export const generateSchedulePreview = (payload) => api.post('/generate-schedule-preview/', payload);
export const finalizeSchedule = (payload) => api.post('/finalize-schedule/', payload);
export const getCurrentSchedule = (seasonId) => api.get(`/seasons/${seasonId}/schedule/`);

export const createPost = (postData) => api.post('/create-post/', postData);
export const listPosts = (postData) => api.get('/posts/');
export const fetchPost = (postId) => api.get(`/posts/${postId}/`);
export const updatePost = (postId, payload) => api.put(`/update-post/${postId}/`);
export const deletePost = (postId) => api.delete(`/delete-post/${postId}/`);

export const updateMatch = (matchId, matchData) => api.put(`/update-match/${matchId}/`, matchData, {
  headers: {
    'Content-Type': 'application/json'
  }
});

export const fetchMatches = (seasonId) =>
  seasonId ? api.get(`/matches/`, { params: { season_id: seasonId } }) : api.get(`/matches/`);

export const uploadFile = (formData) => api.post(`/upload/`, formData, {
  headers: {
    'Content-Type': 'multipart/form-data'
  }
});
export const uploadFiles = (formData) => api.post('/upload-many/', formData, {
  headers: {
    'Content-Type': 'multipart/form-data'
  }
});


export const fetchStandings = (seasonId) => api.get(`/update-standings/${seasonId}/`); // Add this line

export const cleanUpSchedules = () => api.delete('/clean-up-schedules/');


export default api;
